/* eslint-disable lingui/text-restrictions */
import { t } from "@lingui/macro";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

import { useLanguages } from "@/client/services/resume/translation";

type QuestionItemProps = {
  children: ReactNode;
  title: string;
  value: string;
};

const QuestionItem = ({ children, title, value }: QuestionItemProps) => (
  <AccordionItem value={value}>
    <AccordionTrigger className="text-left leading-relaxed">{title}</AccordionTrigger>
    <AccordionContent className="prose max-w-none dark:prose-invert">{children}</AccordionContent>
  </AccordionItem>
);

const LanguageList = () => {
  const { languages } = useLanguages();

  return (
    <div className="flex flex-wrap items-start justify-start gap-x-2 gap-y-4">
      {languages.map((language) => (
        <div key={language.id}>
          <div className="relative bg-secondary-accent font-medium">
            <span className="px-2 py-1">{language.name}</span>

            {language.progress !== undefined && (
              <span
                className={cn(
                  "inset-0 bg-warning px-1.5 py-1 text-xs text-white",
                  language.progress < 40 && "bg-error",
                  language.progress > 80 && "bg-success",
                )}
              >
                {language.progress}%
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export const FAQSection = () => (
  <section id="faq" className="container relative py-24 sm:py-32">
    <div className="grid gap-12 lg:grid-cols-3">
      <div className="space-y-6">
        <h2 className="text-4xl font-bold">{t`Foire aux questions`}</h2>
        <p className="text-base leading-loose">
          {t`Retrouvez les réponses aux questions les plus fréquentes avant de créer votre CV.`}
        </p>
      </div>

      <div className="col-span-2">
        <Accordion collapsible type="single">
          <QuestionItem title={t`À qui s’adresse cet outil de création de CV ?`} value="1">
            <p>
              {t`Cet outil est mis gratuitement à disposition par AcadéNice pour ses étudiants, ses partenaires, ainsi que pour toute personne souhaitant créer un CV clair, professionnel et facile à personnaliser.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`L’outil est-il gratuit ?`} value="2">
            <p>
              {t`Oui. L’utilisation de l’outil est gratuite. AcadéNice le met à disposition pour aider chacun à créer un CV de qualité, sans frais d’accès et sans publicité.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Dois-je créer un compte pour utiliser l’outil ?`} value="3">
            <p>
              {t`Oui. La création d’un compte permet de sauvegarder vos CV, de les modifier plus tard et de retrouver vos modèles personnalisés.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Puis-je créer plusieurs CV ?`} value="4">
            <p>
              {t`Oui. Vous pouvez créer plusieurs versions de votre CV selon vos besoins : alternance, stage, emploi, reconversion, candidature ciblée ou évolution professionnelle.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Puis-je personnaliser mon CV ?`} value="5">
            <p>
              {t`Oui. Vous pouvez choisir un modèle, modifier les couleurs, les polices, la taille des textes, la photo, les sections visibles et l’organisation générale du CV.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Puis-je télécharger mon CV ?`} value="6">
            <p>
              {t`Oui. Une fois votre CV terminé, vous pouvez l’exporter en PDF et l’utiliser librement pour vos candidatures.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Puis-je traduire mon CV dans une autre langue ?`} value="7">
            <p>
              {t`Oui. L’outil propose plusieurs langues disponibles dans l’interface. Vous pouvez consulter les langues actuellement disponibles directement dans cette section et adapter votre CV selon le contexte de votre candidature.`}
            </p>
            <p>{t`Langues actuellement disponibles :`}</p>
            <LanguageList />
          </QuestionItem>

          <QuestionItem title={t`Mes données sont-elles en sécurité ?`} value="8">
            <p>
              {t`Oui. Les informations que vous saisissez sont stockées dans l’outil afin de permettre la création, la modification et la sauvegarde de vos CV. AcadéNice met en place des mesures raisonnables pour protéger ces données.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Comment AcadéNice utilise mes informations ?`} value="9">
            <p>
              {t`Les informations renseignées dans l’outil sont utilisées uniquement pour le fonctionnement du service, l’accompagnement des utilisateurs, le support, le suivi pédagogique ou administratif lorsqu’il concerne AcadéNice. Elles peuvent être traitées par les services internes d’AcadéNice ou reliées à ses outils de suivi, uniquement pour les besoins liés à l’accompagnement, à la formation ou à la relation avec l’utilisateur.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Mes données sont-elles vendues à des tiers ?`} value="10">
            <p>
              {t`Non. Les données saisies dans l’outil ne sont pas vendues à des tiers. Elles ne sont pas utilisées à des fins commerciales externes ni transmises à des organismes sans lien avec les besoins d’AcadéNice.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Qui peut accéder à mes informations ?`} value="11">
            <p>
              {t`Seules les personnes habilitées au sein d’AcadéNice peuvent accéder aux informations nécessaires au fonctionnement de l’outil, au support ou à l’accompagnement. Cet accès est limité aux besoins du service.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`Puis-je demander la suppression de mes données ?`} value="12">
            <p>
              {t`Oui. Vous pouvez demander l’accès, la rectification ou la suppression de vos données personnelles en contactant AcadéNice à l’adresse suivante :`}{" "}
              <a href="mailto:contact@acadenice.fr">contact@acadenice.fr</a>.
            </p>
          </QuestionItem>

          <QuestionItem title={t`Combien de temps mes données sont-elles conservées ?`} value="13">
            <p>
              {t`Les données sont conservées aussi longtemps que votre compte est actif ou que leur conservation reste nécessaire pour l’utilisation du service, le suivi de votre demande ou l’accompagnement proposé par AcadéNice. Vous pouvez demander leur suppression à tout moment.`}
            </p>
          </QuestionItem>

          <QuestionItem title={t`AcadéNice peut-il m’aider à améliorer mon CV ?`} value="14">
            <p>
              {t`Oui. Si vous êtes étudiant, partenaire ou accompagné par AcadéNice, les informations renseignées peuvent faciliter l’aide à la correction, l’amélioration de votre CV ou l’accompagnement dans vos candidatures.`}
            </p>
            <p>
              {t`Vous pouvez également contacter AcadéNice au`}{" "}
              <a href="tel:+33423500270">04 23 50 02 70</a> {t`ou à`}{" "}
              <a href="mailto:recrutement@acadenice.fr">recrutement@acadenice.fr</a>{" "}
              {t`pour échanger avec l’un de nos conseillers en formation et profiter d’une journée gratuite de coaching CV. Nous vous apprenons à utiliser ces outils, à valoriser votre profil et à mieux vous positionner en entreprise.`}
            </p>
          </QuestionItem>
        </Accordion>
      </div>
    </div>
  </section>
);
